import { lazy, Suspense } from 'react';
import { Alert, Card, Col, Empty, Row, Space, Spin, Statistic, Tag, Typography } from 'antd';
import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  FireOutlined,
  ShopOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { dashboardApi, type DashboardActivity } from '../../../api/endpoints/dashboard';
import { PageHeader } from '../../../components/common/PageHeader';
import { useAuthStore } from '../../auth/stores/authStore';

const { Text } = Typography;

const DashboardCharts = lazy(() => import('../components/DashboardCharts'));

export function DashboardPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.summary(),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isError || !data) {
    return <Alert type="error" message="No se pudo cargar el resumen." />;
  }

  return (
    <>
      <PageHeader
        title={`Hola ${user?.firstName ?? ''}`}
        crumbs={[{ title: 'Inicio' }]}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Mis actividades abiertas"
              value={data.counters.myOpenActivities}
              prefix={<CalendarOutlined style={{ color: '#1677ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Cerradas (7 días)"
              value={data.counters.myClosedLast7Days}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Total cuentas"
              value={data.counters.totalAccounts}
              prefix={<BankOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Total contactos"
              value={data.counters.totalPersons}
              prefix={<TeamOutlined style={{ color: '#fa8c16' }} />}
            />
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Ventas en negociación"
              value={data.counters.pendingSales}
              prefix={<ShopOutlined style={{ color: '#13c2c2' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Ganadas este mes"
              value={data.counters.wonSalesCountThisMonth}
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
              suffix={
                data.counters.wonSalesAmountThisMonth
                  ? `· ${parseFloat(data.counters.wonSalesAmountThisMonth).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`
                  : ''
              }
            />
          </Card>
        </Col>
      </Row>

      <Suspense fallback={null}>
        <DashboardCharts charts={data.charts} />
      </Suspense>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <FireOutlined style={{ color: '#ff4d4f' }} />
                <span>Vencidas ({data.myOverdue.length})</span>
              </Space>
            }
          >
            <ActivityList items={data.myOverdue} emptyText="Sin actividades vencidas 🎉" overdue />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: '#1677ff' }} />
                <span>Hoy ({data.myToday.length})</span>
              </Space>
            }
          >
            <ActivityList items={data.myToday} emptyText="Nada programado para hoy" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Próximos 7 días">
            <ActivityList items={data.myUpcoming} emptyText="Sin actividades próximas" />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Mis cuentas favoritas">
            {data.favoriteAccounts.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    Marca cuentas con ★ desde su detalle para verlas aquí.
                  </Text>
                }
              />
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.favoriteAccounts.map((a) => (
                  <li key={a.id} style={{ padding: '6px 0' }}>
                    <Link to={`/cuentas/${a.id}`}>{a.name}</Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}

function ActivityList({
  items,
  emptyText,
  overdue,
}: {
  items: DashboardActivity[];
  emptyText: string;
  overdue?: boolean;
}): JSX.Element {
  if (items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((a) => (
        <li
          key={a.id}
          style={{
            padding: '8px 0',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Space size={8} wrap>
            {a.type && <Tag>{a.type}</Tag>}
            <Link to={`/actividades/${a.id}`}>
              {a.notes?.slice(0, 80) ?? ''}
              {a.notes && a.notes.length > 80 ? '…' : ''}
            </Link>
          </Space>
          <div style={{ fontSize: 12, color: overdue ? '#ff4d4f' : '#888', marginTop: 2 }}>
            {a.startDate ? dayjs(a.startDate).format('DD/MM/YYYY HH:mm') : 'Sin fecha'}
            {a.account && ` · ${a.account.name}`}
            {a.person && ` · ${a.person.firstName ?? ''} ${a.person.lastName}`}
          </div>
        </li>
      ))}
    </ul>
  );
}
